import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import course05 from "../../../course/course-05/course";
import lesson0502 from "../../course-05/lesson-02/lesson";
import lesson0506 from "../../course-05/lesson-06/lesson";
import lesson0218 from "../lesson-18/lesson";
import lesson0221 from "../lesson-21/lesson";
import lesson0222 from "../lesson-22/lesson";

const linkLesson0218 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0218.id);
const linkLesson0221 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0221.id);
const linkLesson0222 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0222.id);
const linkLesson0502 = getLinkForCourseLesson(course05.id, lesson0502.id);
const linkLesson0506 = getLinkForCourseLesson(course05.id, lesson0506.id);

const theory = `
<h3 class=sectionHead>Introduction</h3>
<p>Communication between people through speech is an essential tool for learning, conveying emotions, sharing thoughts. Being able to understand each other’s speech facilitates this communication. In room acoustics especially, speech intelligibility is studied extensively. There is a relationship between the characteristics of the room, the noise, the distance between the receiver and the source and speech intelligibility.
<h3 class=sectionHead>Speech modulation</h3>
<p>
  As pointed out in <a target="_blank" href="${linkLesson0218}">Lesson 2.18</a>: Lombard speech, speech is modulated temporally and spectrally. We know that the spectrum of human speech is in the range from 125&nbsp;Hz up to 10 kHz with the most important frequencies between 125 and 4000&nbsp;Hz. The temporal modulation of speech lies between 0.25 and 32&nbsp;Hz. The modulation frequency peaks lie around 5&nbsp;Hz. One modulation at 32 modulations per second is 31 ms long. It means that the shortest modulation happens in 31 ms time on average. According to Ding et al. [1] speech modulation is very similar for a relatively large number of languages. This can be seen in <a href="#" data-img-alt="Figure 1. The speech modulation spectrum plotted for different languages of speech and different kinds of sources." data-img-src=${config.ASSETS_URL}/02/19/theory/figure-1.svg>Figure 1</a>, where the modulation of speech is plotted horizontally and the normalized amplitude is plotted vertically.<br class=newline><br class=newline>Studying speech modulation is important because there can be an interaction between it and room acoustics. This will be explained in the following chapters and lessons.
  <figure class=figure>
<p><img alt="Figure 1. The speech modulation spectrum plotted for different languages of speech and different kinds of sources." src=${config.ASSETS_URL}/02/19/theory/figure-1.svg><figcaption class=caption><span class=id>Figure 1. The speech modulation spectrum plotted for different languages of speech and different kinds of sources.</span></figcaption></figure>
<p>In room acoustics the practical maximum modulation frequency is assumed to be 20&nbsp;Hz or a minimum length of 50 ms per modulation.
<h3 class=sectionHead>Reverberation</h3>
<p>Reverberation is a combination of many reflections. A reflection is an attenuated, sometimes spatially separated, delayed and coherent copy of the originating sound. These reflections come from reflective surfaces in the environment of the source(s). Some of the reflections arrive at the receiver (e.g., a microphone or a listener) in relatively short time after the direct sound. Due to the precedence effect [2], the direct sound and the early reflections are fused together in the brain. For speech, a limit of 50ms is widely accepted, as it coincides with the length of one modulation at maximum (practical) modulation frequency (20Hz). More information on the relationship between direct, early and late sound in room acoustics can be found in <a target="_blank" href="${linkLesson0502}">Lesson 5.2</a>: How a room responds to sound.
<p>
  Reverberation or late sound is the part of the total sound energy that arrives 50-80 ms after the direct sound. In some rooms with reflective surfaces this reverberation can be quite dominant depending on the position of the source and the receiver. The domination of late reflections reduces speech intelligibility in general because speech that is produced in the moment is masked by speech that was produced a few tenths of a second ago.
  <figure class=figure>
<p><img alt="Figure 2. Diagram showing energy before and after 50 ms." src=${config.ASSETS_URL}/02/19/theory/figure-2.svg><figcaption class=caption><span class=id>Figure 2. Diagram showing energy before and after 50 ms.</span></figcaption></figure>
<p>Let’s consider the reflectogram of a room. It shows the individual reflections in time at a particular point in the room (at the receiver). For good speech intelligibility it is generally accepted that the sum of the energy that arrives at the receiver before 50ms needs to be higher than the energy that arrives later. This ratio is expressed in decibels and is referred to as “Clarity” <em><span class=cmti-10>C</span></em><sub class=textsubscript><span class=cmr-9>50</span></sub>. The more energy is present before 50 ms relatively to after 50 ms, the clearer the sound. A positive number results in a relatively clear sound. A negative number means there is less clarity in the sound and that speech intelligibility is less high. A value of 0&nbsp;dB means that the amount of early and late energy is exactly the same. For “very good” speech intelligibility a minimum value of 6&nbsp;dB is generally required.
<table class=equation>
  <tr>
    <td>
      <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
        <msub>
          <mrow>
            <mi>C</mi>
          </mrow>
          <mrow>
            <mn>50</mn>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mn>10</mn>
        <mo class=MathClass-bin>∗</mo>
        <mi mathvariant=italic>lo</mi>
        <msub>
          <mrow>
            <mi>g</mi>
          </mrow>
          <mrow>
            <mn>10</mn>
          </mrow>
        </msub>
        <mrow>
          <mo fence=true form=prefix>(</mo>
          <mrow>
            <mfrac>
              <mrow>
                <msubsup>
                  <mrow>
                    <mo>∫</mo>
                  </mrow>
                  <mrow>
                    <mn>0</mn>
                    <mi mathvariant=italic>ms</mi>
                  </mrow>
                  <mrow>
                    <mn>50</mn>
                    <mi mathvariant=italic>ms</mi>
                  </mrow>
                </msubsup>
                <msup>
                  <mrow>
                    <mi>p</mi>
                  </mrow>
                  <mrow>
                    <mn>2</mn>
                  </mrow>
                </msup>
                <mo class=MathClass-open>(</mo>
                <mi>t</mi>
                <mo class=MathClass-close>)</mo>
                <mi mathvariant=italic>dt</mi>
              </mrow>
              <mrow>
                <msubsup>
                  <mrow>
                    <mo>∫</mo>
                  </mrow>
                  <mrow>
                    <mn>50</mn>
                    <mi mathvariant=italic>ms</mi>
                  </mrow>
                  <mrow>
                    <mi>∞</mi>
                  </mrow>
                </msubsup>
                <msup>
                  <mrow>
                    <mi>p</mi>
                  </mrow>
                  <mrow>
                    <mn>2</mn>
                  </mrow>
                </msup>
                <mo class=MathClass-open>(</mo>
                <mi>t</mi>
                <mo class=MathClass-close>)</mo>
                <mi mathvariant=italic>dt</mi>
              </mrow>
            </mfrac>
          </mrow>
          <mo fence=true form=postfix>)</mo>
        </mrow>
      </math>
    <td class=eq-no>(1)
</table>
<p>Clarity is not only different depending on the room characteristics and geometry but also on the position of the source and the receiver in the room. The ratio of direct sound to reflected sound is higher near the source, this results in a higher clarity. The distance to a source where the direct sound energy of the source and it’s reflected sound energy (diffuse field energy) are equal can be calculated and is called the reverberation radius or the critical distance. The more reverberant a room is, the smaller the reverberation radius will be. Speech intelligibility will tend to be relatively “good” within this radius. <a target="_blank" href="${linkLesson0506}">Lesson 5.6</a>: How a sound source interacts with a room, goes more into detail about these relationships.
<h3 class=sectionHead>Noise</h3>
<p>Noise is unwanted sound that might decrease speech intelligibility or cause annoyance. In everyday situations noise often comes from different sources - this collection of all noise sources in the scene is often referred to as background noise. The temporal and spectral characteristics can be very irregular. Therefore noise is often averaged and expressed per octave or third-octave band. For speech intelligibility, noise and reverberation have similar consequences. The high the noise level (and the higher the reverberation time), the lower the speech intelligibility. More information on parameters related to speech intelligibility are presented in  <a target="_blank" href="${linkLesson0222}">Lesson 2.22</a>: Introduction to listening experiments, while  <a target="_blank" href="${linkLesson0221}">Lesson 2.21</a>: Assessment of Speech intelligibility (C50,D50,U50,STI), focuses on the perception of sound in adverse conditions.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt id=X0-Ding_2017 class=thebibliography>[1]
  <dd id=bib-1 class=thebibliography>
    <p>Ding, N., Patel, A. D., Chen, L., Butler, H., Luo, C., & Poeppel, D. (2017). Temporal modulations in speech and music. <span class=cmti-10>Neuroscience and Biobehavioral Reviews</span>, 181–187. <a href=https://doi.org/10.1016/j.neubiorev.2017.02.011 class=url>https://doi.org/10.1016/j.neubiorev.2017.02.011</a>
  <dt id=X0-Litovsky_1999 class=thebibliography>[2]
  <dd id=bib-2 class=thebibliography>
    <p>Litovsky, R., Colburn, H., Yost, W., & Guzman, S. (1999). The precedence effect. <span class=cmti-10>The Journal of the Acoustical Society of America</span>, <span class=cmti-10>106</span>(4), 1633–1654. <a href=https://doi.org/10.1121/1.427914 class=url>https://doi.org/10.1121/1.427914</a>
  <dt id=X0-Haas_1951 class=thebibliography>[3]
  <dd id=bib-3 class=thebibliography>
    <p>Haas, H. (1951). Uber den einfluß eines einfachechos auf die hörsamkeit von sprache. <span class=cmti-10>Acta Acustica united with Acustica</span>, <span class=cmti-10>1</span>(2), 49–58.
</dl>
`;

export default theory;
